import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, ChevronDown, Lock, LogOut, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { useAuthStore } from '../../store/authStore';
import { useAvatarUri } from '../../services/avatar';
import Modal from '../ui/Modal';

interface MonitorHeaderProps {
  title: string;
}

export default function MonitorHeader({ title }: MonitorHeaderProps) {
  const profile = useAuthStore((s) => s.profile);
  const reset = useAuthStore((s) => s.reset);
  const navigate = useNavigate();
  // La foto vive en un bucket privado desde la mig 211: hay que pedir permiso
  // de visionado. Si no llega, se pinta el icono genérico de siempre.
  const avatarUri = useAvatarUri(profile?.avatar_url);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modal, setModal] = useState(false);
  // §128 · `null` = no se ha podido contar, y NO es 0: con 0 el globo no se
  // pinta y una campana lisa dice «no tienes avisos». Mismo criterio que el
  // Header de dirección y que el globo de la app.
  const [unread, setUnread] = useState<number | null>(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  // §128 · El globo de la campana. Cuenta solo las PROPIAS sin leer ni
  // descartar: las globales (`user_id IS NULL`) se listan en la página pero no
  // se cuentan aquí porque no tienen `read` por usuario y dejarían el globo
  // encendido para siempre. Es el mismo criterio que el inicio de la app.
  useEffect(() => {
    if (!profile?.id) return;
    const myId = profile.id;
    let cancelled = false;

    async function loadCount() {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', myId)
        .eq('read', false)
        .eq('dismissed', false);
      if (!cancelled) setUnread(error ? null : (count ?? 0));
    }

    void loadCount();
    const channel = supabase
      .channel(`monitor-notifs-unread-${myId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${myId}` },
        () => void loadCount()
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  function closeModal() {
    setModal(false);
    setPwForm({ current: '', next: '', confirm: '' });
    setPwError('');
    setPwDone(false);
    setShowPw({ current: false, next: false, confirm: false });
  }

  async function handleChangePassword() {
    setPwError('');
    if (!pwForm.current) return setPwError('Introduce tu contraseña actual');
    if (pwForm.next.length < 8)
      return setPwError('La nueva contraseña debe tener al menos 8 caracteres');
    if (pwForm.next !== pwForm.confirm) return setPwError('Las contraseñas no coinciden');

    setPwSaving(true);
    const { error: signInErr } = await supabase.auth.signInWithPassword({
      email: profile?.email ?? '',
      password: pwForm.current,
    });
    if (signInErr) {
      setPwSaving(false);
      return setPwError('Contraseña actual incorrecta');
    }
    const { error } = await supabase.auth.updateUser({ password: pwForm.next });
    setPwSaving(false);
    if (error) return setPwError(error.message);
    setPwDone(true);
    setTimeout(closeModal, 2000);
  }

  async function handleLogout() {
    await supabase.auth.signOut();
    reset();
  }

  return (
    <header className="h-16 bg-bg-surface border-b border-bg-border flex items-center justify-between px-8 fixed top-0 left-64 right-0 z-10">
      <h1 className="font-oswald text-xl uppercase tracking-widest text-white">{title}</h1>

      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/monitor/notificaciones')}
          className="relative text-text-secondary hover:text-gold-primary transition-colors"
          aria-label="Mis avisos"
          data-testid="monitor-header-bell"
        >
          <Bell size={20} />
          {unread === null && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-error/20 text-error border border-error/40 text-[10px] font-bold flex items-center justify-center"
              title="No se han podido contar los avisos sin leer"
              data-testid="monitor-header-bell-sin-contar"
            >
              ?
            </span>
          )}
          {(unread ?? 0) > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gold-primary text-bg-primary text-[10px] font-bold flex items-center justify-center"
              data-testid="monitor-header-bell-badge"
            >
              {(unread ?? 0) > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity focus:outline-none"
          >
            {avatarUri ? (
              <img
                src={avatarUri}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-bg-border"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-bg-elevated border border-bg-border flex items-center justify-center">
                <User size={16} className="text-text-secondary" />
              </div>
            )}
            <div className="hidden sm:block text-left">
              <p className="text-text-primary text-sm font-inter font-medium leading-none">
                {profile?.full_name ?? 'Entrenador'}
              </p>
              <p className="text-text-secondary text-xs mt-0.5">Entrenador</p>
            </div>
            <ChevronDown
              size={14}
              className={`text-text-secondary transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 bg-bg-surface border border-bg-border rounded-xl shadow-xl z-50">
              <div className="px-4 py-3 border-b border-bg-border">
                <p className="text-white text-sm font-medium font-inter">{profile?.full_name}</p>
                <p className="text-text-secondary text-xs mt-0.5 font-inter">{profile?.email}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    setModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-text-secondary hover:text-white hover:bg-bg-elevated transition-colors text-left"
                >
                  <Lock size={15} /> Cambiar contraseña
                </button>
                <div className="border-t border-bg-border my-1" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-error hover:bg-error/10 transition-colors text-left"
                >
                  <LogOut size={15} /> Cerrar sesión
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={modal} onClose={closeModal} title="Cambiar contraseña" size="sm">
        {pwDone ? (
          <p className="text-success text-center py-4 font-inter">
            ✅ Contraseña actualizada correctamente
          </p>
        ) : (
          <div className="space-y-4">
            {(['current', 'next', 'confirm'] as const).map((field, i) => (
              <div key={field}>
                <label className="label">
                  {i === 0
                    ? 'Contraseña actual'
                    : i === 1
                      ? 'Nueva contraseña'
                      : 'Confirmar nueva contraseña'}
                </label>
                <div className="relative">
                  <input
                    type={showPw[field] ? 'text' : 'password'}
                    className="input pr-10"
                    value={pwForm[field]}
                    onChange={(e) => setPwForm((f) => ({ ...f, [field]: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => ({ ...s, [field]: !s[field] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
                  >
                    {showPw[field] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            ))}
            {pwError && <p className="text-error text-sm font-inter">{pwError}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={closeModal} className="btn-ghost">
                Cancelar
              </button>
              <button onClick={handleChangePassword} disabled={pwSaving} className="btn-gold">
                {pwSaving ? 'Verificando...' : 'Cambiar contraseña'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </header>
  );
}
